import { useEffect, useState } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/previewPartnerPage.module.scss';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatStatus } from '../helpers/functions';
import { EnvPaths } from '../helpers/environment';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

interface fetchedDataInterface {
  _id: string;
  email: string;
  isVerified: boolean;
  isBlocked: boolean;
  organizationRole: string;
  organizationId: string;
  referralLink: string;
  __v: 3;
  avatarUrl: string;
  firstName: string;
  googleId: string;
  lastName: string;
  waitingForConfirmation: boolean;
  referralLinkVisits: 0;
  updatedAt: Date;
  createdAt: Date;
  totalEarnings: number;
  totalPlansSold: number;
  iframeEarnings: number;
  iframePlansSold: number;
  referralLinkEarnings: number;
  referralLinkPlansSold: number;
  permanentlyRemoved?: boolean;
  emailBeforeDeletion?: string;
}

const PreviewPartnerPage = () => {
  const params = useParams();
  const { id } = params;
  const { user } = useAuth();
  const [fetchedData, setFetchedData] = useState<fetchedDataInterface>();
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation();

  const iframeCode = `<iframe 
  src="${EnvPaths.SITE_URL}/widget?partnerId=${fetchedData?.referralLink}" 
  width="100%" 
  height="100%"
>
  <p>Iframe is not supported in your browser. Please visit <a href="${`${EnvPaths.SITE_URL}/explore-plans?referral=${fetchedData?.referralLink ?? ''}`}">TravelCare</a></p>
</iframe>`;

  const handleCopyToClipboard = async (link: string) => {
    try {
      const referralLink = EnvPaths.SITE_URL + '/explore-plans?referral=' + link;
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(referralLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success(t('previewPartner.handleCopyToClipboard.success'));
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error(t('previewPartner.handleCopyToClipboard.error'));
    }
  };

  const handleCopyIframeCode = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(iframeCode);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = iframeCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success(t('previewPartner.handleCopyToClipboard.success'));
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error(t('previewPartner.handleCopyToClipboard.error'));
    }
  };

  const fetchData = async () => {
    setLoading(true);
    axios
      .get(`${EnvPaths.SERVER_URL}/admin/preview-partner-user/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setFetchedData(res.data.foundMember);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRefetchUserToken = () => {
    axios
      .get(`${EnvPaths.SERVER_URL}/admin/refresh-partner-user-referral-link/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        toast.success(t('previewPartner.handleRefetchUserToken.success'));
        fetchData();
      })
      .catch((err) => {
        console.error(err);
        toast.error(t('previewPartner.handleRefetchUserToken.success'));
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <main className={globalPageStyle.pageContainer}>
      <div className={styles.pageContent}>
        {/*header*/}
        <div className={styles.header}>
          <Link to="/dashboard/partner-program">
            <div className={styles.goBackButton}>
              <img src="/assets/images/back-icon.svg" alt="Go Back" />
            </div>
          </Link>
          <p>{t('previewPartner.goBack')}</p>
        </div>
        {/*header*/}
        {/*body*/}
        <div className={globalPageStyle.pageContent}>
          {/*container flex gap 24*/}
          <div className={styles.overviewContainer}>
            {/*topbar - name, email*/}
            <div className={styles.overviewHeader}>
              <div className={styles.overviewHeaderLeft}>
                <div className={styles.overviewItem}>
                  <p>{t('previewPartner.name')}</p>
                  <p className={styles.overviewItemValue}>{`${fetchedData?.firstName ?? ''} ${fetchedData?.lastName ?? ''}`}</p>
                </div>
                <div className={styles.overviewItem}>
                  <p>{t('previewPartner.email')}</p>
                  <p className={styles.overviewItemValue}>{fetchedData?.emailBeforeDeletion ?? fetchedData?.email ?? ''}</p>
                </div>
                <div className={styles.overviewItem}>
                  <p>{t('previewPartner.userId')}</p>
                  <p className={styles.overviewItemValue}>{fetchedData?._id ?? ''}</p>
                </div>
                <div className={styles.overviewItem}>
                  <p>{t('previewPartner.date')}</p>
                  <p className={styles.overviewItemValue}>{fetchedData?.createdAt ? new Date(fetchedData.createdAt).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div className={styles.overviewItem}>
                  <p>{t('previewPartner.status')}</p>
                  <div className={styles.overviewItemValue}>{formatStatus(t, fetchedData?.permanentlyRemoved ? 'removed' : fetchedData?.isBlocked ? 'deactivated' : 'active')}</div>
                </div>
              </div>
            </div>
            {/*topbar - name, email*/}
            {/*referal link input copy*/}
            <h2>{t('partnerProgram.referralLink')}</h2>
            {!fetchedData?.permanentlyRemoved && (
              <div className={styles.overviewReferralLink}>
                <div className={styles.overviewReferralLinkInput}>
                  <p className={styles.desktop}>{`${EnvPaths.SITE_URL}/explore-plans?referral=${fetchedData?.referralLink ?? ''}`}</p>
                  <p className={styles.mobile}>{`${EnvPaths.SITE_URL}/explore-plans?referral=${fetchedData?.referralLink ?? ''}`?.substring(0, 25) ?? ''}...</p>
                  <p
                    className={styles.overviewReferralLinkInputCopy}
                    onClick={() => {
                      handleCopyToClipboard(fetchedData?.referralLink ?? '');
                    }}
                  >
                    {t('previewPartner.copyLink')}
                  </p>
                </div>
                <button
                  className="primaryButton inverse"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                  onClick={() => {
                    handleRefetchUserToken();
                  }}
                >
                  <img src={'/assets/images/reset.svg'} alt="reset" />
                  <p>{t('previewPartner.resetLink')}</p>
                </button>
              </div>
            )}
            {/*referal link input copy*/}
            {/*statistics*/}
            <div className={globalPageStyle.statistics}>
              {/*Total Referral Link Earnings*/}
              <div className={globalPageStyle.statisticsItem}>
                <p>{t('partnerProgram.referralLinkEarnings')}</p>
                <div className={globalPageStyle.statisticsItemBottom}>
                  <div className={globalPageStyle.statisticsItemBottomLeft}>
                    <p className={globalPageStyle.largeP}>€{(fetchedData?.referralLinkEarnings ?? 0)?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              {/*Total Referral Link Earnings*/}
              {/*Total Referral Link Plans Sold*/}
              <div className={globalPageStyle.statisticsItem}>
                <p>{t('partnerProgram.referralLinkPlansSold')}</p>
                <div className={globalPageStyle.statisticsItemBottom}>
                  <div className={globalPageStyle.statisticsItemBottomLeft}>
                    <p className={globalPageStyle.largeP}>{fetchedData?.referralLinkPlansSold ?? 0}</p>
                  </div>
                </div>
              </div>
              {/*Total Referral Link Plans Sold*/}
            </div>
            {/*statistics*/}
            <h2>Iframe</h2>

            {/* iframe section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ position: 'relative', borderRadius: '10px', backgroundColor: '#F6F8F9', padding: '20px' }}>
                <button
                  style={{
                    position: 'absolute',
                    right: '20px',
                    top: '20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '400',
                    color: 'var(--accent-color)',
                    border: 'none',
                    background: 'none',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                  onMouseOut={(e) => (e.currentTarget.style.textDecoration = 'none')}
                  onClick={handleCopyIframeCode}
                >
                  Copy code
                </button>
                <pre style={{ overflowX: 'auto', fontSize: '14px', lineHeight: '20px' }}>
                  <code>{iframeCode}</code>
                </pre>
              </div>
            </div>
            {/*statistics*/}
            <div className={globalPageStyle.statistics}>
              {/*Total Iframe Earnings*/}
              <div className={globalPageStyle.statisticsItem}>
                <p>{t('partnerProgram.iframeEarnings')}</p>
                <div className={globalPageStyle.statisticsItemBottom}>
                  <div className={globalPageStyle.statisticsItemBottomLeft}>
                    <p className={globalPageStyle.largeP}>€{(fetchedData?.iframeEarnings ?? 0)?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              {/*Total Iframe Earnings*/}
              {/*Total Iframe Plans Sold*/}
              <div className={globalPageStyle.statisticsItem}>
                <p>{t('partnerProgram.iframePlansSold')}</p>
                <div className={globalPageStyle.statisticsItemBottom}>
                  <div className={globalPageStyle.statisticsItemBottomLeft}>
                    <p className={globalPageStyle.largeP}>{fetchedData?.iframePlansSold ?? 0}</p>
                  </div>
                </div>
              </div>
              {/*Total Iframe Plans Sold*/}
            </div>
            {/*statistics*/}
            <h2>Total</h2>
            {/*statistics*/}
            <div className={globalPageStyle.statistics}>
              {/*Total Earnings*/}
              <div className={globalPageStyle.statisticsItem}>
                <p>{t('previewPartner.totalEarnings')}</p>
                <div className={globalPageStyle.statisticsItemBottom}>
                  <div className={globalPageStyle.statisticsItemBottomLeft}>
                    <p className={globalPageStyle.largeP}>€{(fetchedData?.totalEarnings ?? 0)?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              {/*Total Earnings*/}
              {/*Total Plans Sold*/}
              <div className={globalPageStyle.statisticsItem}>
                <p>{t('previewPartner.totalPlansSold')}</p>
                <div className={globalPageStyle.statisticsItemBottom}>
                  <div className={globalPageStyle.statisticsItemBottomLeft}>
                    <p className={globalPageStyle.largeP}>{fetchedData?.totalPlansSold ?? 0}</p>
                  </div>
                </div>
              </div>
              {/*Total Plans Sold*/}
            </div>
            {/*statistics*/}
          </div>
          {/*container flex gap 24*/}
        </div>
        {/*body*/}
      </div>
      <div style={{ height: '100px' }}></div>
    </main>
  );
};
export default PreviewPartnerPage;
